/**
 * Copyright IBM Corp. 2022, 2026
 */

import classnames from 'classnames'
import Image from 'next/image'
import { FlightIcon } from '@hashicorp/mds-react/components'
import s from './event-title.module.css'

interface EventTitleProps {
	title: string
	tagLine: string
	partnerLogos: {
		url: string
		alt: string
		width: number
		height: number
	}[]
	type: 'live' | 'recorded' | 'pending'
}

export default function EventTitle({
	title,
	tagLine,
	partnerLogos,
	type,
}: EventTitleProps) {
	return (
		<div className={s.eventTitle}>
			{partnerLogos?.length > 0 ? (
				<div
					className={classnames(
						s.logoGroup,
						type === 'recorded' && partnerLogos && s.withPartnerLogo,
					)}
				>
					<div className={s.partnerLogos}>
						{partnerLogos.map((logo) => {
							return (
								<Image
									key={logo.url}
									className={s.companyLogo}
									alt={logo.alt}
									src={logo.url}
									width={logo.width}
									height={logo.height}
								/>
							)
						})}
					</div>
				</div>
			) : null}

			<div className={s.textContainer}>
				{type === 'recorded' ? (
					<p className={s.eyebrow}>Recorded Webinar</p>
				) : null}

				<h3
					className={s.title}
					dangerouslySetInnerHTML={{ __html: mitigateWidows(title) }}
				/>

				{type === 'live' ? (
					<div className={s.tagLine}>
						<FlightIcon name="calendar" size={24} />
						<span className="g-type-display-6">{tagLine}</span>
					</div>
				) : null}
			</div>
		</div>
	)
}

/**
 * Avoids leaving words "alone at the end" by replacing spaces with `&nbsp;`
 * @param {string} string - String to modify
 * @param {number} [unbreakLimit=12] - Unbreakable end part won't exceed this length
 * @returns {string} - Modified string with unbreakable end part
 */
function mitigateWidows(string: string, unbreakLimit = 12): string {
	const words = string.split(' ')
	//  Set up an array for unbreakable words
	const unbreakableWords = []
	//  Starting from the end of our words array, begin adding
	//  to unbreakableWords, but avoid exceeding our unbreakLimit
	for (let i = words.length - 1; i >= 0; i--) {
		//  nextLength is how long the unbreakable end part would be if we add this word
		const nextLength = [words[i]].concat(unbreakableWords).join(' ').length
		//  if we would exceed the unbreakLimit, then break out of the for loop
		if (nextLength > unbreakLimit) break
		//  Otherwise, add this word to the unbreakable words array
		//  using unshift() to restore the original word order
		unbreakableWords.unshift(words[i])
	}
	//  We now have some set of unbreakableWords from the end of the words array.
	//  We need to prefix this set with the remaining words from the start of the words array.
	const breakableWords = words.slice(0, words.length - unbreakableWords.length)
	//  Assemble the breakable and unbreakable words into strings
	const breakString = breakableWords.join(' ')
	const unbreakString = unbreakableWords.join('&nbsp;')
	//  It's possible that either breakString and unbreakString are empty;
	//  we need to filter out empty strings before joining to avoid extra spaces
	return [breakString, unbreakString].filter((s) => s !== '').join(' ')
}
